import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MapElement } from '@/lib/shared-types';
import { useApi } from '@/Hooks/useApi';
import { Select } from '@/components/ui/field';
import { Panel } from './components';
import { heatColor, heatRamp, useFormat, type HeatmapResult } from './analytics';

interface FloorScene {
  id: string;
  name: string;
  width: number;
  height: number;
  mapElements: MapElement[];
}

const METRICS = ['revenue', 'occupancy', 'service'] as const;

/**
 * P13-05 / ADR-005 - the heatmap shades the *same* scene the live floor map
 * renders. `/floor-map` returns the document; `/analytics/heatmap` returns one
 * value per MapElement id. There is no second map representation, so an element
 * moved in the editor moves here too, with no analytics-side change.
 */
export function FloorHeatmap({
  branchId,
  from,
  to,
  currency,
}: {
  branchId?: string;
  from: string;
  to: string;
  /** D-02 — null when the selected branches span more than one currency, in
   *  which case `useFormat` renders the values unlabelled rather than picking
   *  an arbitrary code. */
  currency: string | null;
}) {
  const { t } = useTranslation();
  const format = useFormat(currency);

  const [floorId, setFloorId] = useState('');
  const [metric, setMetric] = useState<(typeof METRICS)[number]>('revenue');

  const scenes = useApi<FloorScene[]>('/floor-map', branchId ? { branchId } : undefined);
  const floors = useMemo(() => (Array.isArray(scenes.data) ? scenes.data : []), [scenes.data]);

  useEffect(() => {
    if (floors.length && !floors.some((floor) => floor.id === floorId)) setFloorId(floors[0].id);
  }, [floors, floorId]);

  const heat = useApi<HeatmapResult>(floorId ? '/analytics/heatmap' : null, {
    floorId,
    metric,
    from,
    to,
  });

  const floor = floors.find((candidate) => candidate.id === floorId) ?? null;

  const values = useMemo(() => {
    const map = new Map<string, { value: string | number | null; intensity: number | null; label: string | null }>();
    for (const element of heat.data?.elements ?? []) {
      map.set(element.mapElementId, {
        value: element.value,
        intensity: element.intensity,
        label: element.label,
      });
    }
    return map;
  }, [heat.data]);

  const readValue = (value: string | number | null) => {
    if (value === null) return t('reports.heatmap.noData');
    if (metric === 'revenue') return format.money(value);
    if (metric === 'occupancy') return format.percent(value);
    return format.duration(value);
  };

  return (
    <Panel
      title={t('reports.heatmap.title')}
      description={t('reports.heatmap.description')}
      loading={scenes.loading}
      failed={!!scenes.error}
      empty={!scenes.loading && !floors.length}
      onRetry={scenes.reload}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="h-9 w-auto"
            value={floorId}
            onChange={(event) => setFloorId(event.target.value)}
            aria-label={t('reports.heatmap.floor')}
          >
            {floors.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
          <Select
            className="h-9 w-auto"
            value={metric}
            onChange={(event) => setMetric(event.target.value as (typeof METRICS)[number])}
            aria-label={t('reports.heatmap.metric')}
          >
            {METRICS.map((option) => (
              <option key={option} value={option}>
                {t(`reports.heatmap.metrics.${option}`, option)}
              </option>
            ))}
          </Select>
        </div>
      }
    >
      {floor && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
            <span>
              {heat.data?.higherIsBetter === false
                ? t('reports.heatmap.lowerIsBetter')
                : t('reports.heatmap.higherIsBetter')}
            </span>
            <span className="flex items-center gap-1" aria-hidden>
              {heatRamp.map((color) => (
                <span key={color} className="h-3 w-6 rounded-sm" style={{ background: color }} />
              ))}
            </span>
            <span>
              {t('reports.heatmap.peak')}: {readValue(heat.data?.max ?? null)}
            </span>
          </div>

          <div className="scrollbar-thin overflow-auto">
            <svg
              viewBox={`0 0 ${floor.width} ${floor.height}`}
              className="h-auto w-full min-w-[640px] rounded-lg bg-[var(--muted)]"
              role="img"
              aria-label={t('reports.heatmap.title')}
            >
              {floor.mapElements
                ?.filter((element) => element.visible)
                .slice()
                .sort((a, b) => a.zIndex - b.zIndex)
                .map((element) => {
                  const cell = values.get(element.id);
                  const linked = !!element.entityId;
                  return (
                    <g
                      key={element.id}
                      transform={`translate(${element.x} ${element.y}) rotate(${element.rotation} ${element.width / 2} ${element.height / 2})`}
                    >
                      <rect
                        width={element.width}
                        height={element.height}
                        rx={element.elementType === 'TABLE' ? 10 : 3}
                        fill={linked ? heatColor(cell?.intensity ?? null) : 'var(--border)'}
                        stroke="var(--border)"
                        strokeWidth={1}
                      />
                      {linked && (
                        <title>
                          {`${cell?.label ?? ''} ${readValue(cell?.value ?? null)}`.trim()}
                        </title>
                      )}
                    </g>
                  );
                })}
            </svg>
          </div>

          {/* Identity never rests on colour alone: the same numbers, readable. */}
          <div className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
            {(heat.data?.elements ?? [])
              .filter((element) => element.value !== null && Number(element.value) > 0)
              .slice(0, 12)
              .map((element) => (
                <div key={element.mapElementId} className="flex justify-between gap-2">
                  <span className="truncate text-ink-muted">
                    {element.label ?? element.entityId}
                  </span>
                  <span className="tabular-nums">{readValue(element.value)}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
